<ng-container *transloco="let t">
  <div class="max-w-2xl">
  <!-- Translation Languages -->
  <div>
    <nz-form-label nzRequired class="mb-1">{{ t('settings.preferences.itinerarySettings.translationLanguages') }}</nz-form-label>
    <nz-select
      class="w-full"
      [nzMode]="'multiple'"
      [nzPlaceHolder]="t('settings.preferences.itinerarySettings.placeholders.selectLanguages')"
      [(ngModel)]="selectedLanguages">
      <nz-option
        *ngFor="let lang of orderByName(translationLanguages)"
        [nzLabel]="lang.name"
        [nzValue]="lang.code"></nz-option>
    </nz-select>
  </div>

  <!-- Default Language -->
  <div class="mt-4">
    <nz-form-label nzRequired class="mb-1">{{ t('settings.preferences.itinerarySettings.defaultLanguage') }}</nz-form-label>
    <div class="flex gap-3 items-center">
      <nz-select
        class="flex-1"
        [nzPlaceHolder]="t('settings.preferences.itinerarySettings.placeholders.selectLanguage')"
        [(ngModel)]="selectedDefaultLanguage"
        disabled>
        <nz-option
          *ngFor="let lang of getSelectedLanguagesObjectArray()"
          [nzLabel]="lang?.name"
          [nzValue]="lang.code"></nz-option>
      </nz-select>
      <button
        nz-button
        nzType="primary"
        [disabled]="selectedDefaultLanguage === ''"
        (click)="defaultLanguageSet('default language', true)">
        {{ t('settings.common.updateUppercase') }}
      </button>
    </div>
  </div>

  <div *ngIf="msg === 'default language'" class="mt-2">
    <nz-alert
      *ngIf="actionMessage.display && actionMessage.isAdd"
      [nzType]="actionMessage.type"
      [nzMessage]="actionMessage.message"
      nzShowIcon
      nzCloseable></nz-alert>
  </div>

  <!-- Display Name in Itinerary -->
  <div class="mt-7">
    <h6 class="section-subtitle">{{ t('settings.preferences.itinerarySettings.displayNameInItinerary') }}</h6>
    <div class="mt-4">
      <div class="flex justify-between items-center mb-4">
        <span class="font-medium text-sm">{{ t('settings.preferences.itinerarySettings.showingResultsFor') }} {{ languageType?.name }}</span>
        <nz-select [(ngModel)]="language" [formControl]="languageField">
          <nz-option
            *ngFor="let lang of getSelectedLanguagesDropdown()"
            [nzValue]="lang?.code"
            [nzLabel]="lang?.name"></nz-option>
        </nz-select>
      </div>

      <nz-form-item class="flex mb-4">
        <nz-form-label nzRequired nzFor="title">{{ t('settings.preferences.itinerarySettings.tourOperator') }}</nz-form-label>
        <nz-form-control>
          <input
            [(ngModel)]="title"
            (ngModelChange)="onTitleChange($event)"
            [ngModelOptions]="{standalone: true}"
            type="text"
            class="w-full"
            [placeholder]="t('settings.preferences.itinerarySettings.placeholders.enterTitle')"
            nz-input>
        </nz-form-control>
      </nz-form-item>

      <div *ngIf="!isHidden && languageType?.name !== 'English'">
        <p class="flex items-center font-bold mb-2">
          <app-sparkles-svg></app-sparkles-svg>
          <span class="ai-text-color ml-1 text-base">{{ t('settings.preferences.itinerarySettings.tosgenTranslate') }}</span>
        </p>
        <p
          class="bg-slate-100 cursor-pointer font-semibold rounded-lg p-2 mt-2 mb-4"
          (click)="onTitleChange(translatedTitle); isHidden = true">
          {{ translatedTitle }}
        </p>
        <nz-alert
          class="my-3"
          nzType="info"
          [nzMessage]="t('settings.preferences.itinerarySettings.translationUpdateHint')"
          nzShowIcon
          nzCloseable></nz-alert>
      </div>

      <div class="text-end">
        <button
          nz-button
          nzType="primary"
          [disabled]="title === ''"
          (click)="updateHeaderTitle()">
          {{ t('settings.common.update') }}
        </button>
      </div>

      <div *ngIf="msg === 'display name'" class="my-2">
        <nz-alert
          *ngIf="actionMessage.display && actionMessage.isAdd"
          [nzType]="actionMessage.type"
          [nzMessage]="actionMessage.message"
          nzShowIcon
          nzCloseable></nz-alert>
      </div>
    </div>
  </div>

  <!-- AI Integration -->
  <div class="mt-8" *appHasAnyPrivilege="'SETTING.PREFERENCES.AI.INTEGRATION'">
    <h6 class="section-subtitle">{{ t('settings.preferences.itinerarySettings.aiIntegration.title') }}</h6>
    <p class="text-sm text-gray-600 mt-1 mb-4">
      {{ t('settings.preferences.itinerarySettings.aiIntegration.description') }}
    </p>
    <nz-form-label nzFor="aiApiKey">{{ t('settings.preferences.itinerarySettings.aiIntegration.apiKeyLabel') }}</nz-form-label>
    <nz-form-item class="flex mb-0">
      <nz-form-control class="flex-1">
        <div class="flex gap-3 items-start">
          <div class="flex-1">
            <nz-input-group [nzSuffix]="suffixTemplate" class="w-full">
              <input
                nz-input
                id="aiApiKey"
                [type]="showApiKey ? 'text' : 'password'"
                [formControl]="aiApiKeyControl"
                [placeholder]="t('settings.preferences.itinerarySettings.aiIntegration.apiKeyPlaceholder')"
                class="w-full" />
            </nz-input-group>
            <ng-template #suffixTemplate>
              <span
                nz-icon
                [nzType]="showApiKey ? 'eye' : 'eye-invisible'"
                class="cursor-pointer text-gray-400"
                (click)="showApiKey = !showApiKey"></span>
            </ng-template>
            <p *ngIf="aiApiKeyControl.errors?.['noWhitespace'] && aiApiKeyControl.dirty" class="text-xs text-red-500 mt-1">
              {{ t('settings.preferences.itinerarySettings.aiIntegration.noWhitespaceError') }}
            </p>
            <p *ngIf="hasExistingKey && aiApiKeyControl.value?.trim() !== ''" class="text-xs text-gray-500 mt-1">
              {{ t('settings.preferences.itinerarySettings.aiIntegration.encryptedNotice') }}
            </p>
          </div>
          <button
            nz-button
            nzType="primary"
            [disabled]="aiApiKeyControl.invalid || isSavingKey"
            [nzLoading]="isSavingKey"
            (click)="onSaveApiKey()">
            {{ t('settings.preferences.itinerarySettings.aiIntegration.saveKey') }}
          </button>
          <button
            nz-button
            [disabled]="isSavingKey && hasExistingKey"
            (click)="onRemoveApiKey()">
            {{ t('settings.preferences.itinerarySettings.aiIntegration.removeKey') }}
          </button>
          </div>
        </nz-form-control>
      </nz-form-item>
      <div *ngIf="msg === 'api key'" class="my-2">
        <nz-alert
          *ngIf="actionMessage.display && actionMessage.isAdd"
          [nzType]="actionMessage.type"
          [nzMessage]="actionMessage.message"
          nzShowIcon
          nzCloseable></nz-alert>
      </div>
      <div *ngIf="!hasExistingKey" class="mt-4 bg-blue-50 rounded-xl p-4 flex items-center gap-3">
        <span nz-icon nzType="info-circle" nzTheme="fill" class="text-purple-600 text-lg flex-shrink-0"></span>
        <span class="text-sm text-gray-700 flex flex-wrap items-center gap-x-1">
          {{ t('settings.preferences.itinerarySettings.aiIntegration.openAPIBannerOne') }}
          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" class="inline-flex items-center text-purple-600 hover:text-purple-700">
            {{ t('settings.preferences.itinerarySettings.aiIntegration.openAPIBannerTwo') }} <span class="underline ml-1">{{ t('settings.preferences.itinerarySettings.aiIntegration.openAPIBannerThree') }}</span>
            <lucide-icon name="external-link" class="icon-size ml-1"></lucide-icon>
          </a>
        </span>
      </div>
    </div>


    <!-- Tour Date Categorization by Season -->
    <div class="mt-12" *appHasAnyPrivilege="'SETTING.PREFERENCES.SEASON'">
      <div class="flex justify-between items-start mb-4">
        <div>
          <h6 class="section-subtitle">{{ t('settings.preferences.itinerarySettings.seasonCategorization.title') }}</h6>
          <p class="text-sm text-gray-600 mt-1">
            {{ t('settings.preferences.itinerarySettings.seasonCategorization.description') }}
          </p>
        </div>
        <button nz-button class="flex items-center" nzType="primary" (click)="addSeason()">
          <span nz-icon nzType="plus" nzTheme="outline"></span>
          {{ t('settings.preferences.itinerarySettings.seasonCategorization.addSeason') }}
        </button>
      </div>

      <form nz-form nzLayout="vertical" #seasonForm="ngForm" (ngSubmit)="onUpdateSeasons(seasonForm)">
        <div *ngFor="let season of seasonCategory; let i = index" class="flex gap-4 items-start mb-3">
          <nz-form-item class="mb-0 flex-1 min-w-0">
            <nz-form-label *ngIf="i === 0">{{ t('settings.preferences.itinerarySettings.seasonCategorization.seasonName') }}</nz-form-label>
            <nz-form-control [nzValidateStatus]="nameCtrl" [nzErrorTip]="t('settings.preferences.itinerarySettings.seasonCategorization.seasonNameRequired')">
              <input
                nz-input
                [(ngModel)]="season.name"
                [name]="'seasonName' + i"
                #nameCtrl="ngModel"
                required
                type="text"
                class="w-full"
                [placeholder]="t('settings.preferences.itinerarySettings.seasonCategorization.seasonNamePlaceholder')" />
            </nz-form-control>
          </nz-form-item>
          <nz-form-item class="mb-0 flex-1 min-w-0">
            <nz-form-label *ngIf="i === 0">{{ t('settings.preferences.itinerarySettings.seasonCategorization.selectMonthRange') }}</nz-form-label>
            <div class="flex gap-2">
              <nz-form-control class="flex-1 min-w-0" [nzValidateStatus]="startCtrl" [nzErrorTip]="t('settings.preferences.itinerarySettings.seasonCategorization.startMonthRequired')">
                <nz-select
                  class="w-full"
                  [(ngModel)]="season.startMonth"
                  [name]="'startMonth' + i"
                  #startCtrl="ngModel"
                  required
                  [nzPlaceHolder]="t('settings.preferences.itinerarySettings.seasonCategorization.startMonth')">
                  <nz-option
                    *ngFor="let month of months"
                    [nzLabel]="month.label"
                    [nzValue]="month.value"></nz-option>
                </nz-select>
              </nz-form-control>
              <nz-form-control class="flex-1 min-w-0" [nzValidateStatus]="endCtrl" [nzErrorTip]="t('settings.preferences.itinerarySettings.seasonCategorization.endMonthRequired')">
                <nz-select
                  class="w-full"
                  [(ngModel)]="season.endMonth"
                  [name]="'endMonth' + i"
                  #endCtrl="ngModel"
                  required
                  [nzPlaceHolder]="t('settings.preferences.itinerarySettings.seasonCategorization.endMonth')">
                  <nz-option
                    *ngFor="let month of months"
                    [nzLabel]="month.label"
                    [nzValue]="month.value"></nz-option>
                </nz-select>
              </nz-form-control>
            </div>
          </nz-form-item>
          <nz-form-item class="mb-0 flex-shrink-0">
            <nz-form-label *ngIf="i === 0" class="invisible">.</nz-form-label>
            <nz-form-control>
              <button
                type="button"
                nzDanger
                nz-button
                nzType="default"
                (click)="removeSeason(i)">
                <span nz-icon nzType="delete" nzTheme="outline"></span>
              </button>
            </nz-form-control>
          </nz-form-item>
        </div>

        <div class="text-end mt-4">
          <button
            type="submit"
            nz-button
            nzType="primary"
            [disabled]="seasonForm.invalid">
            {{ t('settings.common.update') }}
          </button>
        </div>
      </form>

      <div *ngIf="msg === 'season category'" class="my-2">
        <nz-alert
          *ngIf="actionMessage.display && actionMessage.isAdd"
          [nzType]="actionMessage.type"
          [nzMessage]="actionMessage.message"
          nzShowIcon
          nzCloseable></nz-alert>
      </div>
    </div>
    </div>
</ng-container>
